'use client';

import React from 'react';
import {
  IconShieldCheck,
  IconBrain,
  IconChartBar,
  IconRobot,
  IconArrowsSplit,
  IconClipboardCheck,
  IconArrowUpRight,
  IconSparkles,
  IconCode,
  IconRocket,
  IconTarget,
  IconTopologyStarRing3,
  IconUsers,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';

export interface TrainingPerkItem {
  id?: number | string;
  text?: string;
  prise_en_main?: string;
  category?: string;
  title?: string;
  description?: string;
  icon?: string;
}

interface TrainingPresentationGridProps {
  perks: TrainingPerkItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  isService?: boolean;
}

// Icon resolver helper
const getIconComponent = (iconName?: string, index: number = 0) => {
  const iconLower = (iconName || '').toLowerCase();

  if (iconLower.includes('shield') || iconLower.includes('secur') || iconLower.includes('donnee') || iconLower.includes('complian') || iconLower.includes('rgpd')) {
    return IconShieldCheck;
  }
  if (iconLower.includes('brain') || iconLower.includes('ia') || iconLower.includes('ai') || iconLower.includes('prompt') || iconLower.includes('predict') || iconLower.includes('model')) {
    return IconBrain;
  }
  if (iconLower.includes('chart') || iconLower.includes('data') || iconLower.includes('fichier') || iconLower.includes('stat') || iconLower.includes('roi') || iconLower.includes('kpi') || iconLower.includes('analytic')) {
    return IconChartBar;
  }
  if (iconLower.includes('bot') || iconLower.includes('robot') || iconLower.includes('gpt') || iconLower.includes('agent') || iconLower.includes('chat') || iconLower.includes('conversat')) {
    return IconRobot;
  }
  if (iconLower.includes('workflow') || iconLower.includes('auto') || iconLower.includes('link') || iconLower.includes('tache') || iconLower.includes('crm') || iconLower.includes('pipel')) {
    return IconArrowsSplit;
  }
  if (iconLower.includes('clip') || iconLower.includes('regle') || iconLower.includes('charte') || iconLower.includes('check') || iconLower.includes('certif') || iconLower.includes('audit') || iconLower.includes('diag')) {
    return IconClipboardCheck;
  }
  if (iconLower.includes('code') || iconLower.includes('api') || iconLower.includes('dev') || iconLower.includes('react') || iconLower.includes('next') || iconLower.includes('stack')) {
    return IconCode;
  }
  if (iconLower.includes('rocket') || iconLower.includes('launch') || iconLower.includes('mvp') || iconLower.includes('deploy') || iconLower.includes('saas')) {
    return IconRocket;
  }
  if (iconLower.includes('user') || iconLower.includes('client') || iconLower.includes('team') || iconLower.includes('support') || iconLower.includes('portal')) {
    return IconUsers;
  }
  if (iconLower.includes('target') || iconLower.includes('goal') || iconLower.includes('focus') || iconLower.includes('impact')) {
    return IconTarget;
  }
  if (iconLower.includes('spark') || iconLower.includes('email') || iconLower.includes('mail') || iconLower.includes('content') || iconLower.includes('doc') || iconLower.includes('ocr') || iconLower.includes('pdf')) {
    return IconSparkles;
  }

  // Fallback cycle matching the screenshot sequence
  const defaultIcons = [
    IconShieldCheck,
    IconBrain,
    IconChartBar,
    IconRobot,
    IconArrowsSplit,
    IconClipboardCheck,
    IconTopologyStarRing3,
    IconSparkles,
  ];

  return defaultIcons[index % defaultIcons.length];
};

// Formatter to render **bold text** inside descriptions
const formatDescriptionWithBold = (text: string) => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-neutral-900 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

export const TrainingPresentationGrid = ({
  perks,
  locale = 'fr',
  title,
  subtitle,
  isService = false,
}: TrainingPresentationGridProps) => {
  if (!perks || perks.length === 0) return null;

  const defaultBadge =
    locale === 'fr'
      ? (isService ? 'Prestations & Livrables' : 'Programme détaillé')
      : (isService ? 'Deliverables & Capabilities' : 'Detailed Curriculum');

  const defaultTitle =
    locale === 'fr'
      ? (isService ? 'Ce qui est inclus dans le service' : 'Au programme de la formation')
      : (isService ? 'What is Included in This Service' : 'Training Curriculum & Modules');

  const defaultSubtitle =
    locale === 'fr'
      ? (isService ? 'Des prestations sur-mesure et concrètes pour accélérer votre transformation' : 'Des modules concrets et immédiatement applicables à votre activité')
      : (isService ? 'Practical deliverables and tailored solutions designed for immediate business impact' : 'Practical modules designed for real-world impact and immediate adoption');

  // Normalize each item from either structured fields or text string
  const normalizedItems = perks.map((perk, index) => {
    let category = perk.prise_en_main || perk.category || '';
    let itemTitle = perk.title || '';
    let description = perk.description || '';
    let iconName = perk.icon || '';

    // If title is missing, attempt parsing from `text`
    if (!itemTitle && perk.text) {
      if (perk.text.includes('|')) {
        const parts = perk.text.split('|').map((p) => p.trim());
        if (parts.length >= 3) {
          category = parts[0];
          itemTitle = parts[1];
          description = parts.slice(2).join(' | ');
        } else if (parts.length === 2) {
          itemTitle = parts[0];
          description = parts[1];
        } else {
          itemTitle = parts[0];
        }
      } else if (perk.text.includes(':')) {
        const parts = perk.text.split(':').map((p) => p.trim());
        itemTitle = parts[0];
        description = parts.slice(1).join(': ');
      } else {
        itemTitle = perk.text;
      }
    }

    // Default category if none was set
    if (!category) {
      if (isService) {
        category = index < 2
          ? (locale === 'fr' ? 'FONCTIONNALITÉS CLÉS' : 'CORE CAPABILITIES')
          : (locale === 'fr' ? 'LIVRABLES & SUIVI' : 'DELIVERABLES & SUPPORT');
      } else {
        category = index < 3 ? 'PRISE EN MAIN' : 'USAGE AVANCÉ';
      }
    }

    return {
      id: perk.id || index,
      category,
      title: itemTitle,
      description,
      iconName,
      rawIndex: index,
    };
  });

  return (
    <div className="relative mt-12 rounded-3xl overflow-hidden bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800 p-6 sm:p-8 md:p-12 shadow-xl shadow-[#301754]/5">
      {/* Discreet background ambient lighting */}
      <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-[#c02029]/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Header */}
      <div className="relative z-10 mb-8 sm:mb-10 text-left">
        <span className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest text-[#c02029] uppercase bg-[#c02029]/10 border border-[#c02029]/20 rounded-full">
          {defaultBadge}
        </span>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#2e1452] dark:text-white tracking-tight">
          {title || defaultTitle}
        </h3>
        <p className="mt-2 text-sm sm:text-base font-medium text-[#3b2d56] dark:text-neutral-300 max-w-2xl">
          {subtitle || defaultSubtitle}
        </p>
      </div>

      {/* Grid of presentation cards */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
        {normalizedItems.map((item, index) => {
          const IconComponent = getIconComponent(
            item.iconName || item.title + ' ' + item.description,
            index
          );

          // Highlight particular cards with amber/gold accents (e.g. 6th card in screenshot)
          const isGoldAccent = index === 5 || item.iconName?.includes('gold');

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
              className="group relative flex flex-col justify-between rounded-2xl bg-white dark:bg-neutral-900 hover:bg-white dark:hover:bg-neutral-800 border border-[#e5e2f5] hover:border-[#c02029]/50 dark:border-neutral-800 dark:hover:border-[#c02029]/60 p-6 transition-all duration-300 hover:-translate-y-1 shadow-md shadow-[#301754]/5 hover:shadow-xl hover:shadow-[#c02029]/10"
            >
              <div>
                {/* Top bar with icon and diagonal arrow */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-200 group-hover:scale-105 ${
                      isGoldAccent
                        ? 'bg-amber-500/15 border border-amber-400/60 text-amber-600 dark:text-amber-300'
                        : 'bg-rose-50 dark:bg-[#c02029]/15 border border-[#c02029]/25 text-[#c02029] group-hover:bg-[#c02029] group-hover:text-white'
                    }`}
                  >
                    <IconComponent className="w-5 h-5 stroke-[1.75]" />
                  </div>
                  <div className="text-[#c02029]/50 group-hover:text-[#c02029] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200">
                    <IconArrowUpRight className="w-5 h-5 stroke-[2]" />
                  </div>
                </div>

                {/* Category tag */}
                <div className="text-[11px] font-bold tracking-wider text-[#c02029] uppercase mb-2">
                  {item.category}
                </div>

                {/* Card Title */}
                <h4 className="text-base sm:text-lg font-bold text-[#2e1452] dark:text-white tracking-tight leading-snug mb-2 group-hover:text-[#c02029] transition-colors">
                  {item.title}
                </h4>
              </div>

              {/* Subtitle / Description */}
              {item.description ? (
                <p className="mt-2 text-xs sm:text-sm text-[#5e5873] dark:text-neutral-300 leading-relaxed">
                  {formatDescriptionWithBold(item.description)}
                </p>
              ) : (
                <div className="h-2" />
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
